import {naturalSort} from '@benchling/js-utils';
import React, {useState} from 'react';

import {
  CHEMICAL_FORMATS,
  EXPORT_DISPLAY_NAME,
  REACTION_FORMATS,
  ChemicalFileFormat,
  CHEMICAL_FILE_FORMAT_TO_EXTENSION,
} from 'chemistry/ChemistryConstants';
import {CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {MarvinCompatibleFileFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {molConvert, MolConvertOutput} from 'chemistry/molecules/MoleculeDAO';
import {
  chemicalStructureFromFile,
  downloadChemicalStructure,
} from 'notebook/notes/attachments/molecules/MoleculeUtil';
import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {splitExtension, toFilename} from 'util/FileUtil';

export interface DownloadChemicalModalProps {
  sourceFormat: MarvinCompatibleFileFormat;
  filename: string;
  externalFile: ExternalFile;
  reaction: boolean;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function DownloadChemicalModal({
  sourceFormat,
  filename,
  externalFile,
  reaction,
  onRequestClose,
  onRequestSubmit,
}: DownloadChemicalModalProps): JSX.Element {
  const supportedDownloadFormats = reaction
    ? REACTION_FORMATS
    : [...CHEMICAL_FORMATS, ChemicalFileFormat.SDF];
  const [downloadFormat, setDownloadFormat] = useState<ChemicalFileFormat>(sourceFormat);

  async function handleDownload(): Promise<void> {
    try {
      const chemicalStructure = await chemicalStructureFromFile(externalFile, sourceFormat);
      let convertedOutput: MolConvertOutput;

      if (downloadFormat === ChemicalFileFormat.SDF) {
        convertedOutput = await molConvert(
          CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[sourceFormat],
          // @ts-expect-error: Special-case: we do not include SDF as a valid format but we allow exporting it.
          ChemicalFileFormat.SDF,
          chemicalStructure
        );
      } else {
        convertedOutput = await molConvert(
          CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[sourceFormat],
          CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[downloadFormat],
          chemicalStructure
        );
      }
      const exportedStructure = convertedOutput.structure ?? convertedOutput.binaryStructure;

      if (exportedStructure == null) {
        throw new Error('Failed to convert chemical structure.');
      }

      const downloadFilename = toFilename(
        splitExtension(filename).root,
        CHEMICAL_FILE_FORMAT_TO_EXTENSION[downloadFormat]
      );
      downloadChemicalStructure(exportedStructure, downloadFilename, downloadFormat);
      onRequestSubmit();
    } catch (err) {
      throw new Error('Unable to download structure.');
    }
  }

  return (
    <Modal
      title="Download chemical file"
      onRequestSubmit={handleDownload}
      onRequestClose={onRequestClose}
      buttons={[{text: 'Download', submit: true}]}
    >
      <p>
        You're about to download the following chemical file: <b>{filename}</b>. Please select a file format
        to continue.
      </p>
      <FieldLabel label="Format" isRequired={true} />
      <FormGroup>
        <SelectDropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={downloadFormat}>
              {naturalSort(supportedDownloadFormats).map((formatOption: ChemicalFileFormat) => (
                <Option value={formatOption} key={formatOption} onSelect={setDownloadFormat}>
                  {EXPORT_DISPLAY_NAME[formatOption]}
                </Option>
              ))}
            </Menu>
          }
          displayName={EXPORT_DISPLAY_NAME[downloadFormat]}
        />
      </FormGroup>
    </Modal>
  );
}
